/**
 * Who may see which half of Workforce.
 *
 * Supervisor and Operations Executive are the same site role — same labour
 * screens, same wages, same reports. Accounts may look at the wage bill
 * but does not mark the muster. HR never sees site labour.
 *
 * Sidebar / hub IA is five groups (Overview, Site, Payments, Compliance,
 * Settings) so nothing sits more than a few items deep.
 */

export const WORKFORCE_GROUPS = ['Overview', 'Site', 'Payments', 'Compliance', 'Settings'];

/**
 * The workforce splits in two before it splits by anything else.
 *
 * Grouping by function first — Site, Payments, Compliance — put the labour
 * muster and the employee register under one heading, and a builder looking
 * for one had to read past the other. A workman and a salaried employee are
 * paid on different terms by different people, so the track comes first and
 * the sub-category second.
 */
export const WORKFORCE_TRACKS = ['Overview', 'Employees', 'Labour', 'Settings'];

export const TRACK_BLURB = {
  Overview: 'Where the whole workforce stands today.',
  Employees: 'Office and site staff, paid a salary.',
  Labour: 'Site workmen, marked at a muster and paid a wage.',
  Settings: 'Applies to both.'
};

/** Sub-categories, in the order they appear under a track. */
export const TRACK_GROUPS = {
  Overview: ['Overview'],
  Employees: ['People', 'Attendance', 'Payroll', 'Records'],
  Labour: ['People', 'Muster', 'Payments', 'Compliance'],
  Settings: ['Settings']
};

/**
 * Which track and sub-category each path belongs to.
 *
 * Kept as one table rather than spread across the nav arrays so the whole
 * information architecture can be read in one place — and so a path that is
 * added without being placed shows up immediately as unplaced rather than
 * quietly landing at the bottom of something.
 */
export const PLACEMENT = {
  '/builder/workforce': ['Overview', 'Overview'],

  '/builder/hr/employees': ['Employees', 'People'],
  '/builder/hr/attendance': ['Employees', 'Attendance'],
  '/builder/hr/leave': ['Employees', 'Attendance'],
  '/builder/hr/site-approvals': ['Employees', 'Attendance'],
  '/builder/hr/payroll': ['Employees', 'Payroll'],
  '/builder/hr/payroll/setup': ['Employees', 'Payroll'],
  '/builder/hr/policies': ['Employees', 'Records'],
  '/builder/hr/vault': ['Employees', 'Records'],
  '/builder/hr/office-locations': ['Employees', 'Records'],

  '/builder/operations/labour': ['Labour', 'People'],
  '/builder/operations/labour/gangs': ['Labour', 'People'],
  '/builder/operations/labour/contractors': ['Labour', 'People'],
  '/builder/operations/labour/muster': ['Labour', 'Muster'],
  '/builder/operations/labour/approvals': ['Labour', 'Muster'],
  '/builder/operations/labour/anomalies': ['Labour', 'Muster'],
  '/builder/operations/labour/wages': ['Labour', 'Payments'],
  '/builder/operations/labour/spend': ['Labour', 'Payments'],
  '/builder/operations/labour/payment-approvals': ['Labour', 'Payments'],
  '/builder/operations/labour/registers': ['Labour', 'Compliance'],
  '/builder/operations/labour/compliance': ['Labour', 'Compliance'],
  // Covers both tracks, and sits with the other statutory output rather than
  // being duplicated under each.
  '/builder/workforce/reports': ['Labour', 'Compliance'],

  '/builder/notifications/preferences': ['Settings', 'Settings'],
  // Owner only — added by WorkforceHub once GET /approvals/me says so.
  '/builder/workforce/approvers': ['Settings', 'Settings']
};

/** Where a path sits, or null if nobody has placed it. */
export function placementFor(path) {
  return PLACEMENT[path] ?? null;
}

export function designationKey(name) {
  return (name || '').trim().toLowerCase();
}

export function isSupervisorDesignation(name) {
  return designationKey(name) === 'supervisor';
}

/** Supervisor is the Operations Executive title. Engineer is a different role. */
export function isOpsExecutiveDesignation(name) {
  const key = designationKey(name);
  return (
    key === 'supervisor' ||
    key === 'operation' ||
    key === 'operations executive' ||
    key === 'ops executive'
  );
}

/**
 * May RECORD money against labour — payments, advances, fines, corrections.
 *
 * Operations (including Supervisor) and Management. Accounts may only look.
 */
export function canManageLabourWages(designationName, vertical) {
  const v = (vertical || '').toLowerCase();
  return v === 'management' || v === 'operations';
}

/**
 * May LOOK at the wage bill.
 *
 * Wider than `canManageLabourWages` because Accounts now has read access to
 * labour, wages and the statutory registers: an accountant reconciling a month
 * has to see where site cash went, and until now could not.
 *
 * The two are deliberately separate rather than one flag. Recording a payment
 * is a decision about what somebody is owed, made by the people responsible for
 * the muster; reconciling it afterwards is not. The server enforces the same
 * split — Accounts is granted can_view and nothing else — so this only decides
 * which buttons are worth drawing.
 */
export function canViewLabourWages(designationName, vertical) {
  if (canManageLabourWages(designationName, vertical)) {
    return true;
  }
  return (vertical || '').toLowerCase() === 'accounts';
}

export const SITE_LABOUR_NAV = [
  {
    displayName: 'Labour register',
    title: 'Labour register',
    blurb: 'Everyone on your sites — in-house and contractor.',
    path: '/builder/operations/labour',
    group: 'Site',
    supervisor: true,
    accounts: true
  },
  {
    displayName: 'Muster roll',
    title: 'Muster roll',
    blurb: "Review a site's day — punched, absent, unmarked.",
    path: '/builder/operations/labour/muster',
    group: 'Site',
    supervisor: true
  },
  {
    displayName: 'Groups',
    title: 'Groups & shifts',
    blurb: 'Groups who punch together, and night shifts that wrap midnight.',
    path: '/builder/operations/labour/gangs',
    group: 'Site',
    supervisor: true
  },
  {
    displayName: 'Approvals',
    title: 'Muster approvals',
    blurb: 'Contractor-marked attendance waiting on an engineer.',
    path: '/builder/operations/labour/approvals',
    group: 'Site',
    supervisor: true
  },
  {
    displayName: 'Wages',
    title: 'Wages',
    blurb: 'What is owed, paid, and still open.',
    path: '/builder/operations/labour/wages',
    group: 'Payments',
    supervisor: true,
    accounts: true
  },
  {
    displayName: 'Spend',
    title: 'Labour spend',
    blurb: 'Cost by site, trade, contractor and month.',
    path: '/builder/operations/labour/spend',
    group: 'Payments',
    supervisor: true,
    accounts: true
  },
  {
    displayName: 'Payment approvals',
    title: 'Payment approvals',
    blurb: 'Payments held for a second person before they move a balance.',
    path: '/builder/operations/labour/payment-approvals',
    group: 'Payments',
    supervisor: true,
    accounts: true
  },
  {
    displayName: 'Registers',
    title: 'Statutory registers',
    blurb: 'CLRA forms for the labour inspector.',
    path: '/builder/operations/labour/registers',
    group: 'Compliance',
    supervisor: true,
    accounts: true
  },
  {
    displayName: 'Reports',
    title: 'Reports',
    blurb: 'Daily and monthly attendance, muster grid and payouts.',
    path: '/builder/workforce/reports',
    group: 'Compliance',
    supervisor: true,
    accounts: true
  },
  {
    displayName: 'Anomalies',
    title: 'Attendance anomalies',
    blurb: 'Days where the hours did not add up cleanly.',
    path: '/builder/operations/labour/anomalies',
    group: 'Compliance',
    supervisor: true,
    accounts: false
  },
  {
    displayName: 'Minimum wage',
    title: 'Minimum wage compliance',
    blurb: 'Notified figures, and who is paid below them.',
    path: '/builder/operations/labour/compliance',
    group: 'Compliance',
    supervisor: true,
    accounts: true
  },
  {
    displayName: 'Contractors',
    title: 'Contractors',
    blurb: 'Dues rolled up per contractor.',
    path: '/builder/operations/labour/contractors',
    group: 'Settings',
    supervisor: true,
    accounts: true
  }
];

export const OFFICE_HR_NAV = [
  {
    displayName: 'Employees',
    title: 'Employees',
    blurb: 'Staff directory and statutory records.',
    path: '/builder/hr/employees',
    group: 'Site',
    hr: true,
    management: true
  },
  {
    displayName: 'Attendance',
    title: 'Attendance',
    blurb: 'Office punch register and corrections.',
    path: '/builder/hr/attendance',
    group: 'Site',
    hr: true,
    management: true
  },
  {
    displayName: 'Leave',
    title: 'Leave',
    blurb: 'Balances, requests and approvals.',
    path: '/builder/hr/leave',
    group: 'Site',
    hr: true,
    management: true
  },
  {
    displayName: 'Site approvals',
    title: 'Site approvals',
    blurb: 'Confirm staff who punched in away from an office.',
    path: '/builder/hr/site-approvals',
    group: 'Site',
    hr: true,
    management: true
  },
  {
    displayName: 'Payroll',
    title: 'Payroll',
    blurb: 'Run payslips for in-house staff.',
    path: '/builder/hr/payroll',
    group: 'Payments',
    hr: 'manager',
    management: true,
    accounts: true
  },
  {
    // The page existed with no way in: no menu entry and no link from
    // Payroll, so the salary components payroll is computed from could not be
    // set up without typing the address.
    displayName: 'Payroll setup',
    title: 'Payroll setup',
    blurb: 'Salary components and statutory rules — PF, ESI, professional tax.',
    path: '/builder/hr/payroll/setup',
    group: 'Payments',
    hr: 'manager',
    management: true,
    accounts: true
  },
  {
    displayName: 'Policies',
    title: 'Policies',
    blurb: 'Circulate policies and see who has accepted them.',
    path: '/builder/hr/policies',
    group: 'Compliance',
    hr: true,
    management: true
  },
  {
    displayName: 'Document vault',
    title: 'Document vault',
    blurb: 'Company documents, and employee documents to verify.',
    path: '/builder/hr/vault',
    group: 'Compliance',
    hr: true,
    management: true
  },
  {
    displayName: 'Office locations',
    title: 'Office locations',
    blurb: 'Where office staff may mark attendance from.',
    path: '/builder/hr/office-locations',
    group: 'Settings',
    hr: true,
    management: true
  }
];

const OVERVIEW_NAV = [
  {
    displayName: 'Dashboard',
    title: 'Dashboard',
    blurb: 'Shortcuts into every workforce group.',
    path: '/builder/workforce',
    group: 'Overview'
  }
];

const SHARED_SETTINGS_NAV = [
  {
    displayName: 'Preferences',
    title: 'Preferences',
    blurb: 'Notification preferences for workforce alerts.',
    path: '/builder/notifications/preferences',
    group: 'Settings'
  }
];

/**
 * Insert `{ group }` header rows wherever the group changes, in
 * WORKFORCE_GROUPS order. Empty groups are skipped.
 */
export function withGroupHeaders(items) {
  const result = [];
  for (const group of WORKFORCE_GROUPS) {
    const inGroup = items.filter((item) => item.group === group);
    if (!inGroup.length) continue;
    result.push({ group });
    for (const item of inGroup) {
      result.push({ displayName: item.displayName, path: item.path });
    }
  }
  return result;
}

/**
 * Which site-labour entries a person should be offered.
 *
 * Accounts gets the money and nothing that marks attendance — they
 * reconcile the muster, they do not keep it. Operations (including
 * Supervisor) and everyone else get all of it.
 */
function visibleSiteLabour(designationName, vertical) {
  if ((vertical || '').toLowerCase() === 'accounts') {
    return SITE_LABOUR_NAV.filter((item) => item.accounts);
  }
  return SITE_LABOUR_NAV;
}

function visibleOfficeHr(designationName, vertical) {
  const v = (vertical || '').toLowerCase();
  const isHrManager = designationKey(designationName) === 'hr manager';

  if (v === 'hr') {
    return OFFICE_HR_NAV.filter((item) => {
      if (item.hr === 'manager') return isHrManager;
      return item.hr;
    });
  }
  if (v === 'accounts') {
    return OFFICE_HR_NAV.filter((item) => item.accounts);
  }
  if (v === 'operations') {
    return [];
  }
  // Management / builder / default — full office half
  return OFFICE_HR_NAV.filter((item) => item.management);
}

export function siteLabourNavFor(designationName, vertical) {
  return visibleSiteLabour(designationName, vertical).map(({ displayName, path, group }) => ({
    displayName,
    path,
    group
  }));
}

export function siteLabourCardsFor(designationName, vertical) {
  return visibleSiteLabour(designationName, vertical).map(
    ({ title, blurb, path, group, displayName }) => ({
      title,
      blurb,
      path,
      group,
      displayName
    })
  );
}

export function officeHrNavFor(designationName, vertical) {
  return visibleOfficeHr(designationName, vertical).map(({ displayName, path, group }) => ({
    displayName,
    path,
    group
  }));
}

export function officeHrCardsFor(designationName, vertical) {
  return visibleOfficeHr(designationName, vertical).map(
    ({ title, blurb, path, group, displayName }) => ({
      title,
      blurb,
      path,
      group,
      displayName
    })
  );
}

/**
 * Full Workforce sidebar children for a role, with Overview / Site / Payments /
 * Compliance / Settings headers. Overview + Preferences are always included
 * when the person has any other workforce link.
 */
/**
 * Paths kept directly in the sidebar.
 *
 * Everything else moved to the Workforce dashboard. The sidebar was listing
 * all twenty screens, and once the dashboard groups the same twenty by track
 * the two say the same thing in two different shapes — which is worse than
 * either alone, because the one you learn is whichever you happened to open.
 *
 * Only the dashboard now (29 Sep): Muster roll was taken out of the sidebar
 * on request — it is one tap away under Workforce → Labour → Muster.
 */
const SIDEBAR_DIRECT = ['/builder/workforce'];

/**
 * What the sidebar offers under Workforce.
 *
 * The dashboard first, then the handful worth reaching without a stop on the
 * way. Role filtering is unchanged and still lives in the same two functions,
 * so somebody who cannot see the muster does not get it here either.
 */
export function workforceNavFor(designationName, vertical) {
  const v = (vertical || '').toLowerCase();
  const labour = v === 'hr' ? [] : siteLabourNavFor(designationName, vertical);
  const office = officeHrNavFor(designationName, vertical);
  const items = [...OVERVIEW_NAV, ...labour, ...office, ...SHARED_SETTINGS_NAV];

  const kept = SIDEBAR_DIRECT.map((path) =>
    items.find((item) => item.path === path)
  ).filter(Boolean);

  // No group headers: one entry does not need dividing into sections.
  return kept.map((item) => ({
    displayName: item.displayName,
    path: item.path
  }));
}

/** The full grouped list, still used anywhere that wants every entry. */
export function workforceNavAllFor(designationName, vertical) {
  const v = (vertical || '').toLowerCase();
  const labour = v === 'hr' ? [] : siteLabourNavFor(designationName, vertical);
  const office = officeHrNavFor(designationName, vertical);
  return withGroupHeaders([
    ...OVERVIEW_NAV,
    ...labour,
    ...office,
    ...SHARED_SETTINGS_NAV
  ]);
}

/**
 * Hub cards keyed by the same five groups, for the Workforce landing page.
 */
export function workforceHubSectionsFor(designationName, vertical) {
  const v = (vertical || '').toLowerCase();
  const labour = v === 'hr' ? [] : siteLabourCardsFor(designationName, vertical);
  const office = officeHrCardsFor(designationName, vertical);
  const items = [...OVERVIEW_NAV, ...labour, ...office, ...SHARED_SETTINGS_NAV];

  const shaped = items.map((item) => {
    const [track, group] = placementFor(item.path) ?? ['Labour', 'Compliance'];
    return {
      track,
      group,
      title: item.title || item.displayName,
      blurb: item.blurb,
      path: item.path
    };
  });

  return WORKFORCE_TRACKS.map((track) => ({
    track,
    blurb: TRACK_BLURB[track],
    groups: (TRACK_GROUPS[track] ?? [])
      .map((group) => ({
        group,
        items: shaped.filter((i) => i.track === track && i.group === group)
      }))
      .filter((g) => g.items.length > 0)
  })).filter((t) => t.groups.length > 0);
}

/**
 * Where an Operations login should land.
 *
 * Supervisor and Operations Executive share the dashboard (plans, today,
 * inventory) and reach labour from Workforce. Engineer is the same vertical
 * and lands here too. Labour stays one click away for everybody.
 */
export function operationsLoginPath(designationName) {
  return '/builder/dashboard';
}
